import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Chip,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Switch,
  FormControlLabel,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
  Badge,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  PhotoCamera as PhotoCameraIcon,
  Draw as DrawIcon,
  LocationOn as LocationIcon,
  Settings as SettingsIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  WhatsApp as WhatsAppIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Description as DescriptionIcon,
  Close as CloseIcon
} from '@mui/icons-material';

import { usuarioService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

// Configuração da API
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://trajetus.net';

const isRemoteHttpUrl = (value: any): value is string => {
  if (!value || typeof value !== 'string') return false;
  const v = value.trim();
  return v.startsWith('http://') || v.startsWith('https://') || v.startsWith('https//') || v.startsWith('http//');
};

// Helper: normaliza valor BIT/Buffer/numérico para booleano
const normalizeBitToBool = (value: any): boolean => {
  try {
    if (value && typeof value === 'object') {
      const data = (value as any).data;
      if (Array.isArray(data)) {
        return data[0] === 1;
      }
    }
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'boolean') return value;
    return !!value;
  } catch {
    return !!value;
  }
};

const resolveImageSrc = (value?: string | null): string | undefined => {
  if (!value || typeof value !== 'string') return undefined;
  const v = value.trim();
  if (!v) return undefined;
  if (v.startsWith('data:')) {
    const idx = v.indexOf(';base64,');
    if (idx >= 0) {
      const payload = v.substring(idx + ';base64,'.length).trim();
      if (payload.startsWith('http') || payload.startsWith('https//') || payload.startsWith('http//')) {
        let url = payload;
        if (url.startsWith('https//')) url = `https://${url.substring('https//'.length)}`;
        if (url.startsWith('http//')) url = `http://${url.substring('http//'.length)}`;
        url = url.replace('ppswhatsappnet', 'pps.whatsapp.net').replace('whatsappnet', 'whatsapp.net');
        return url;
      }
    }
    return v;
  }
  if (v.startsWith('blob:')) return v;
  if (v.startsWith('http://') || v.startsWith('https://')) return v;
  if (v.startsWith('https//')) return `https://${v.substring('https//'.length)}`;
  if (v.startsWith('http//')) return `http://${v.substring('http//'.length)}`;
  if (v.startsWith('/')) {
    const base = (API_BASE_URL || '').replace(/\/$/, '');
    if (v.startsWith('/uploads/') || v.includes('/uploads/')) {
      return `${base}${v}`;
    }
    return v;
  }
  if (v.includes('/')) {
    const base = (API_BASE_URL || '').replace(/\/$/, '');
    return `${base}/${v.replace(/^\//, '')}`;
  }

  return `data:image/jpeg;base64,${v}`;
};

const extractUploadedFotoUrl = (resp: any): string | undefined => {
  const candidates = [
    resp?.data?.data?.foto,
    resp?.data?.data?.url,
    resp?.data?.data?.path,
    resp?.data?.foto,
    resp?.data?.url,
    resp?.foto,
    resp?.url,
    resp?.path
  ];
  const v = candidates.find((x) => typeof x === 'string' && x.trim() !== '');
  return v ? String(v) : undefined;
};

const extractWhatsAppLink = (resultado: any): string | undefined => {
  if (!resultado) return undefined;
  if (typeof resultado.link === 'string' && resultado.link.trim() !== '') {
    return resultado.link.trim();
  }
  if (resultado.link && typeof resultado.link === 'object') {
    const values = Object.values(resultado.link).filter(
      (x) => typeof x === 'string' && x.trim() !== ''
    );
    if (values.length > 0) return String(values[0]).trim();
  }
  return undefined;
};

interface Usuario {
  id: number;
  user_name: string;
  email: string;
  celular: string;
  cpf: string;
  rg: string;
  crp: string;
  role: string;
  ativo: boolean;
  id_nivel_acesso: number;
  foto: string;
  foto_url?: string;
  assinatura: string;
  data_nascimento: string;
  plano_adesao: string;
  tipo_pagamento: string;
  valor_mensalidade: number;
  cep: string;
  endereco: string;
  numero: string;
  bairro: string;
  cidade: string;
  uf: string;
  instancia_whatsapp: string;
  token_whatsapp: string;
  envio_mensagens_whatsapp: boolean;
  login_duas_etapas: boolean;
  contrato: string;
  login_duas_etapas_whatsapp: boolean;
}

interface NivelAcesso {
  id: number;
  nome: string;
  descricao: string;
}

interface WhatsAppRemindersForm {
  dayBeforeEnabled: boolean;
  dayBeforeTime: string;
  dayOfEnabled: boolean;
  dayOfTime: string;
  hoursBeforeEnabled: boolean;
  hoursBeforeAmount: string;
  messageTemplate: string;
}

const DEFAULT_WHATS_REMINDER_TEMPLATE =
  'Olá {{paciente}}! 😊\n\nGostaria de confirmar seu atendimento psicológico com {{psicologo}} agendado para {{data}} às {{hora}} 🗓️🕒\n\nVocê pode, por gentileza, confirmar sua presença? ✔️\nCaso precise remarcar, é só me avisar 💬\n\nFico no aguardo! 🌿';

const HORARIO_OPCOES: string[] = (() => {
  const result: string[] = [];
  for (let h = 6; h <= 23; h += 1) {
    for (let m = 0; m < 60; m += 30) {
      if (h === 23 && m > 0) continue;
      const hh = h.toString().padStart(2, '0');
      const mm = m.toString().padStart(2, '0');
      result.push(`${hh}:${mm}`);
    }
  }
  return result;
})();

const Usuarios: React.FC = () => {
  const navigate = useNavigate();
  const { user: authUser, updateUser } = useAuth();

  // Estados
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [niveisAcesso, setNiveisAcesso] = useState<NivelAcesso[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [showMapModal, setShowMapModal] = useState(false);
  const [pendingFotoFile, setPendingFotoFile] = useState<File | null>(null);
  const [pendingFotoPreviewUrl, setPendingFotoPreviewUrl] = useState<string | null>(null);
  const [whatsStatus, setWhatsStatus] = useState<string>('DISCONNECTED');
  const [whatsHasSession, setWhatsHasSession] = useState(false);
  const [whatsQrCode, setWhatsQrCode] = useState<string | null>(null);
  const [whatsPhone, setWhatsPhone] = useState<string | null>(null);
  const [whatsConnectedAt, setWhatsConnectedAt] = useState<string | null>(null);
  const [whatsLoading, setWhatsLoading] = useState(false);
  const [whatsReminders, setWhatsReminders] = useState<WhatsAppRemindersForm>({
    dayBeforeEnabled: false,
    dayBeforeTime: '',
    dayOfEnabled: false,
    dayOfTime: '',
    hoursBeforeEnabled: false,
    hoursBeforeAmount: '',
    messageTemplate: DEFAULT_WHATS_REMINDER_TEMPLATE
  });
  const [whatsTestNumber, setWhatsTestNumber] = useState<string>('');
  const [whatsTestMessage, setWhatsTestMessage] = useState<string>('');
  const [whatsSendingTest, setWhatsSendingTest] = useState(false);

  // Estados do formulário
  const [formData, setFormData] = useState({
    user_name: '',
    email: '',
    celular: '',
    cpf: '',
    rg: '',
    crp: '',
    role: '',
    senha: '',
    ativo: true,
    id_nivel_acesso: '',
    foto: '',
    foto_url: '',
    assinatura: '',
    data_nascimento: '',
    plano_adesao: '',
    tipo_pagamento: '',
    valor_mensalidade: '',
    cep: '',
    endereco: '',
    numero: '',
    bairro: '',
    cidade: '',
    uf: '',
    instancia_whatsapp: '',
    token_whatsapp: '',
    envio_mensagens_whatsapp: false,
    login_duas_etapas: false,
    contrato: '',
    login_duas_etapas_whatsapp: false
  });

  // Estados de validação
  const [fieldErrors, setFieldErrors] = useState({
    email: false,
    celular: false,
    cpf: false,
    cep: false,
    data_nascimento: false,
    senha: false
  });

  // Carregar dados
  useEffect(() => {
    carregarDados();
  }, []);

  useEffect(() => {
    if (activeTab === 3 && editingId) {
      carregarWhatsAppStatus(editingId);
    }
  }, [activeTab, editingId]);

  const carregarDados = async () => {
    setLoading(true);
    try {
      // Carregar usuários
      const usuariosResponse = await usuarioService.listar();

      // Corrigir estrutura de resposta - acessar response.data.data primeiro
      const usuariosData = usuariosResponse?.data?.data || usuariosResponse?.data || usuariosResponse || [];
      const usuariosArray = Array.isArray(usuariosData) ? usuariosData : [];
      const usuariosNormalizados = usuariosArray.map((u: any) => ({
        ...u,
        ativo: normalizeBitToBool(u?.ativo)
      }));
      setUsuarios(usuariosNormalizados);

      // Carregar níveis de acesso
      try {
        const niveisResponse = await fetch(`${API_BASE_URL}/api/niveis-acesso`);
        const niveisData = await niveisResponse.json();
        setNiveisAcesso(Array.isArray(niveisData?.data) ? niveisData.data : []);
      } catch (niveisError) {
        console.warn('Erro ao carregar níveis de acesso:', niveisError);
        setNiveisAcesso([]);
      }

    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      setError('Erro ao carregar dados. Verifique se o servidor está rodando.');
      setUsuarios([]);
    } finally {
      setLoading(false);
    }
  };

  const carregarWhatsAppStatus = async (userId: number) => {
    try {
      setWhatsLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/whatsapp-baileys/${userId}/status`);
      const data = await response.json();
      if (!data || data.success === false) {
        setWhatsStatus('DISCONNECTED');
        setWhatsHasSession(false);
        setWhatsQrCode(null);
        setWhatsPhone(null);
        setWhatsConnectedAt(null);
        setWhatsReminders({
          dayBeforeEnabled: false,
          dayBeforeTime: '',
          dayOfEnabled: false,
          dayOfTime: '',
          hoursBeforeEnabled: false,
          hoursBeforeAmount: '',
          messageTemplate: ''
        });
        if (data && data.message) {
          setError(data.message);
        }
        return;
      }
      const status = data.status || data.dbStatus || 'DISCONNECTED';
      setWhatsStatus(status);
      setWhatsHasSession(!!data.hasSession);
      setWhatsQrCode(data.qrCode || null);
      setWhatsPhone(data.whatsappPhone || null);
      setWhatsConnectedAt(data.connectedAt || null);
      const r = data.reminders || {};
      const templateFromDb =
        r.messageTemplate && String(r.messageTemplate).trim().length > 0
          ? r.messageTemplate
          : DEFAULT_WHATS_REMINDER_TEMPLATE;
      setWhatsReminders({
        dayBeforeEnabled: !!r.dayBeforeEnabled,
        dayBeforeTime: r.dayBeforeTime || '',
        dayOfEnabled: !!r.dayOfEnabled,
        dayOfTime: r.dayOfTime || '',
        hoursBeforeEnabled: !!r.hoursBeforeEnabled,
        hoursBeforeAmount: r.hoursBeforeAmount != null ? String(r.hoursBeforeAmount) : '',
        messageTemplate: templateFromDb
      });
      if (!whatsTestNumber && formData.celular) {
        setWhatsTestNumber(formData.celular);
      }
      if (!whatsTestMessage && templateFromDb) {
        setWhatsTestMessage(templateFromDb);
      }
    } catch (err: any) {
      console.error('Erro ao carregar status do WhatsApp:', err);
      setError('Erro ao carregar status do WhatsApp Baileys.');
      setWhatsStatus('DISCONNECTED');
      setWhatsHasSession(false);
      setWhatsQrCode(null);
      setWhatsPhone(null);
      setWhatsConnectedAt(null);
    } finally {
      setWhatsLoading(false);
    }
  };

  const iniciarSessaoWhatsApp = async () => {
    if (!editingId) return;
    try {
      setWhatsLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/whatsapp-baileys/${editingId}/qrcode`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (!data || data.success === false) {
        setError(data?.message || 'Erro ao iniciar sessão do WhatsApp.');
      }
      await new Promise((resolve) => setTimeout(resolve, 2000));
      await carregarWhatsAppStatus(editingId);
    } catch (err: any) {
      console.error('Erro ao iniciar sessão do WhatsApp:', err);
      setError('Erro ao iniciar sessão do WhatsApp Baileys.');
    } finally {
      setWhatsLoading(false);
    }
  };

  const desconectarWhatsApp = async () => {
    if (!editingId) return;
    try {
      setWhatsLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/whatsapp-baileys/${editingId}/disconnect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (!data || data.success === false) {
        setError(data?.message || 'Erro ao desconectar WhatsApp.');
      }
      await carregarWhatsAppStatus(editingId);
    } catch (err: any) {
      console.error('Erro ao desconectar WhatsApp:', err);
      setError('Erro ao desconectar WhatsApp Baileys.');
    } finally {
      setWhatsLoading(false);
    }
  };

  const salvarConfigLembretesWhatsApp = async () => {
    if (!editingId) return;
    try {
      setWhatsLoading(true);
      const payload = {
        dayBeforeEnabled: whatsReminders.dayBeforeEnabled,
        dayBeforeTime: whatsReminders.dayBeforeTime || null,
        dayOfEnabled: whatsReminders.dayOfEnabled,
        dayOfTime: whatsReminders.dayOfTime || null,
        hoursBeforeEnabled: whatsReminders.hoursBeforeEnabled,
        hoursBeforeAmount: whatsReminders.hoursBeforeEnabled
          ? parseInt(whatsReminders.hoursBeforeAmount || '0', 10) || 0
          : 0,
        messageTemplate: whatsReminders.messageTemplate || null
      };
      const response = await fetch(`${API_BASE_URL}/api/whatsapp-baileys/${editingId}/reminders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!data || data.success === false) {
        setError(data?.message || 'Erro ao salvar lembretes do WhatsApp.');
      } else {
        setSuccess('Configurações de lembretes salvas com sucesso.');
        setTimeout(() => setSuccess(''), 3000);
      }
      await carregarWhatsAppStatus(editingId);
    } catch (err: any) {
      console.error('Erro ao salvar lembretes do WhatsApp:', err);
      setError('Erro ao salvar configuração de lembretes do WhatsApp Baileys.');
    } finally {
      setWhatsLoading(false);
    }
  };

  const enviarMensagemTesteWhatsApp = async () => {
    if (!editingId) return;
    const numeroDestino = (whatsTestNumber || formData.celular || '').trim();
    if (!numeroDestino) {
      setError('Informe um número de celular para envio de teste.');
      return;
    }
    const mensagem =
      (whatsTestMessage || '').trim() ||
      (whatsReminders.messageTemplate || '').trim() ||
      'Mensagem de teste do Trajetus.';
    try {
      setWhatsSendingTest(true);
      const response = await fetch(`${API_BASE_URL}/api/whatsapp-baileys/${editingId}/send-test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          to: numeroDestino,
          message: mensagem
        })
      });
      const data = await response.json();
      if (!data || data.success === false) {
        setError(data?.message || 'Erro ao enviar mensagem de teste.');
      } else {
        setSuccess('Mensagem de teste enviada com sucesso.');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err: any) {
      console.error('Erro ao enviar mensagem de teste:', err);
      setError('Erro ao enviar mensagem de teste via WhatsApp Baileys.');
    } finally {
      setWhatsSendingTest(false);
    }
  };

  const getWhatsStatusLabel = (status: string) => {
    if (status === 'CONNECTED') return 'Conectado';
    if (status === 'QR_READY') return 'QR disponível';
    if (status === 'STARTING') return 'Iniciando';
    if (status === 'RECONNECTING') return 'Reconectando';
    return 'Desconectado';
  };

  const getWhatsStatusColor = (status: string): 'default' | 'success' | 'warning' | 'error' | 'info' => {
    if (status === 'CONNECTED') return 'success';
    if (status === 'QR_READY') return 'info';
    if (status === 'STARTING' || status === 'RECONNECTING') return 'warning';
    return 'default';
  };

  // Funções do formulário
  const handleNew = () => {
    if (pendingFotoPreviewUrl) {
      try { URL.revokeObjectURL(pendingFotoPreviewUrl); } catch { /* noop */ }
    }
    setPendingFotoFile(null);
    setPendingFotoPreviewUrl(null);

    setFormData({
      user_name: '',
      email: '',
      celular: '',
      cpf: '',
      rg: '',
      crp: '',
      role: '',
      senha: '',
      ativo: true,
      id_nivel_acesso: '',
      foto: '',
      foto_url: '',
      assinatura: '',
      data_nascimento: '',
      plano_adesao: '',
      tipo_pagamento: '',
      valor_mensalidade: '',
      cep: '',
      endereco: '',
      numero: '',
      bairro: '',
      cidade: '',
      uf: '',
      instancia_whatsapp: '',
      token_whatsapp: '',
      envio_mensagens_whatsapp: false,
      login_duas_etapas: false,
      contrato: '',
      login_duas_etapas_whatsapp: false
    });
    setFieldErrors({
      email: false,
      celular: false,
      cpf: false,
      cep: false,
      data_nascimento: false,
      senha: false
    });
    setEditingId(null);
    setActiveTab(0);
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleEdit = (usuario: Usuario) => {
    console.log(' [DEBUG] Editando usuário:', usuario.user_name);
    console.log(' [DEBUG] Foto do usuário:', usuario.foto ? 'Presente (' + usuario.foto.length + ')' : 'Ausente');
    console.log(' [DEBUG] Assinatura do usuário:', usuario.assinatura ? 'Presente (' + (typeof usuario.assinatura) + ' - ' + (usuario.assinatura.length || 'sem length') + ')' : 'Ausente');
    console.log(' [DEBUG] Contrato do usuário:', usuario.contrato ? 'Presente (' + usuario.contrato.length + ')' : 'Ausente/Null');
    console.log(' [DEBUG] Contrato raw:', usuario.contrato);
    console.log(' [DEBUG] Assinatura raw:', usuario.assinatura);
    if (usuario.assinatura && typeof usuario.assinatura === 'object') {
      console.log(' [DEBUG] Propriedades da assinatura:', Object.keys(usuario.assinatura));
      const assinaturaObj = usuario.assinatura as any;
      console.log(' [DEBUG] Assinatura.data:', assinaturaObj.data ? 'Presente (' + assinaturaObj.data.length + ')' : 'Ausente');
    }

    setFormData({
      user_name: usuario.user_name || '',
      email: usuario.email || '',
      celular: usuario.celular || '',
      cpf: usuario.cpf || '',
      rg: usuario.rg || '',
      crp: usuario.crp || '',
      role: usuario.role || '',
      senha: '',
      ativo: normalizeBitToBool(usuario.ativo), // Garantir conversão robusta
      id_nivel_acesso: usuario.id_nivel_acesso?.toString() || '',
      foto: usuario.foto || '',
      foto_url: (usuario as any)?.foto_url || '',
      assinatura: (() => {
        console.log(' [DEBUG] Processando assinatura para formData:', typeof usuario.assinatura);
        if (typeof usuario.assinatura === 'string') {
          console.log(' [FRONTEND] Assinatura é string, usando diretamente');
          return usuario.assinatura;
        } else if (usuario.assinatura && typeof usuario.assinatura === 'object') {
          console.log(' [FRONTEND] Assinatura é objeto, tentando converter...');
          const assinaturaObj = usuario.assinatura as any;
          if (assinaturaObj.data) {
            // Converter Buffer/objeto para base64
            const bytes = new Uint8Array(assinaturaObj.data);
            const binary = String.fromCharCode(...Array.from(bytes));
            const base64 = btoa(binary);
            console.log(' [FRONTEND] Assinatura convertida de objeto para base64');
            return `data:image/png;base64,${base64}`;
          }
        }
        console.log(' [FRONTEND] Assinatura vazia ou inválida');
        return '';
      })(),
      data_nascimento: usuario.data_nascimento ? usuario.data_nascimento.split(' ')[0] : '',
      plano_adesao: usuario.plano_adesao || '',
      tipo_pagamento: usuario.tipo_pagamento || '',
      valor_mensalidade: usuario.valor_mensalidade?.toString() || '',
      cep: usuario.cep || '',
      endereco: usuario.endereco || '',
      numero: usuario.numero || '',
      bairro: usuario.bairro || '',
      cidade: usuario.cidade || '',
      uf: usuario.uf || '',
      instancia_whatsapp: usuario.instancia_whatsapp || '',
      token_whatsapp: usuario.token_whatsapp || '',
      envio_mensagens_whatsapp: Boolean(usuario.envio_mensagens_whatsapp),
      login_duas_etapas: Boolean(usuario.login_duas_etapas),
      contrato: usuario.contrato || '',
      login_duas_etapas_whatsapp: Boolean(usuario.login_duas_etapas_whatsapp)
    });

    console.log(' [DEBUG] FormData após carregar:', {
      foto: usuario.foto ? 'Carregada (' + usuario.foto.length + ')' : 'Não carregada',
      assinatura: usuario.assinatura ? 'Carregada (' + usuario.assinatura.length + ')' : 'Não carregada'
    });

    // Log específico da assinatura convertida
    const formDataAssinatura = (() => {
      if (typeof usuario.assinatura === 'string') {
        return usuario.assinatura;
      } else if (usuario.assinatura && typeof usuario.assinatura === 'object') {
        const assinaturaObj = usuario.assinatura as any;
        if (assinaturaObj.data) {
          const bytes = new Uint8Array(assinaturaObj.data);
          const binary = String.fromCharCode(...Array.from(bytes));
          const base64 = btoa(binary);
          return `data:image/png;base64,${base64}`;
        }
      }
      return '';
    })();

    console.log(' [DEBUG] Assinatura convertida para formData:', formDataAssinatura ? 'Sucesso (' + formDataAssinatura.length + ')' : 'Falhou');

    setEditingId(usuario.id);
    setActiveTab(0);
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleCancel = () => {
    if (pendingFotoPreviewUrl) {
      try { URL.revokeObjectURL(pendingFotoPreviewUrl); } catch { /* noop */ }
    }
    setPendingFotoFile(null);
    setPendingFotoPreviewUrl(null);

    setShowForm(false);
    setEditingId(null);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.user_name.trim()) {
      setError('Nome é obrigatório');
      return;
    }

    if (!formData.email.trim()) {
      setError('Email é obrigatório');
      return;
    }

    if (!editingId && !formData.senha.trim()) {
      setError('Senha é obrigatória para novos usuários');
      return;
    }

    setLoading(true);
    try {
      console.log(' [DEBUG] Dados do formulário antes do envio:', formData);
      console.log(' [DEBUG] Assinatura no formData:', formData.assinatura ? 'Presente (' + (typeof formData.assinatura === 'string' ? formData.assinatura.length : 'não é string') + ')' : 'Ausente');

      const dados: any = {
        user_name: formData.user_name,
        email: formData.email,
        celular: formData.celular || null,
        cpf: formData.cpf || null,
        rg: formData.rg || null,
        crp: formData.crp || null,
        role: formData.role || null,
        ativo: formData.ativo ? 1 : 0, // Convertendo boolean para bit(1)
        id_nivel_acesso: formData.id_nivel_acesso ? parseInt(formData.id_nivel_acesso) : null,
        foto: (() => {
          const v = formData.foto;
          if (!v || typeof v !== 'string' || v.trim() === '') return null;
          if (v.startsWith('data:')) return null;
          if (pendingFotoPreviewUrl && v === pendingFotoPreviewUrl) return null;
          return v;
        })(),
        assinatura: formData.assinatura || null,
        data_nascimento: formData.data_nascimento || null,
        plano_adesao: formData.plano_adesao || null,
        tipo_pagamento: formData.tipo_pagamento || null,
        valor_mensalidade: formData.valor_mensalidade ? parseFloat(formData.valor_mensalidade) : null,
        cep: formData.cep || null,
        endereco: formData.endereco || null,
        numero: formData.numero || null,
        bairro: formData.bairro || null,
        cidade: formData.cidade || null,
        uf: formData.uf || null,
        instancia_whatsapp: formData.instancia_whatsapp || null,
        token_whatsapp: formData.token_whatsapp || null,
        envio_mensagens_whatsapp: formData.envio_mensagens_whatsapp ? 1 : 0,
        login_duas_etapas: formData.login_duas_etapas ? 1 : 0,
        contrato: formData.contrato || null,
        login_duas_etapas_whatsapp: formData.login_duas_etapas_whatsapp ? 1 : 0
      };

      if (formData.senha.trim()) {
        dados.senha = formData.senha;
      }

      console.log(' [DEBUG] Dados preparados para envio:', dados);
      console.log(' [DEBUG] Assinatura nos dados preparados:', dados.assinatura ? 'Presente (' + dados.assinatura.length + ')' : 'Ausente/Null');
      console.log(' [DEBUG] Contrato nos dados preparados:', dados.contrato ? 'Presente (' + dados.contrato.length + ')' : 'Ausente/Null');
      console.log(' [DEBUG] Tipo de operação:', editingId ? 'Atualização' : 'Criação');

      if (editingId) {
        // Para edição, sempre incluir assinatura mesmo que seja null
        if (!dados.hasOwnProperty('assinatura')) {
          dados.assinatura = null;
        }
        console.log(' [DEBUG] Dados finais para atualização:', dados);
        console.log(' [DEBUG] Chamando API PUT /api/usuarios/' + editingId);
        const resultado = await usuarioService.atualizar(editingId, dados);
        console.log(' [DEBUG] Resposta da API:', resultado);

        let uploadedFotoUrl: string | undefined;

        if (pendingFotoFile) {
          const uploadResp = await usuarioService.uploadFoto(editingId, pendingFotoFile);
          const url = extractUploadedFotoUrl(uploadResp);
          uploadedFotoUrl = url;
          if (uploadedFotoUrl) {
            setFormData(prev => ({ ...prev, foto: uploadedFotoUrl as string }));
          }
          if (pendingFotoPreviewUrl) {
            try { URL.revokeObjectURL(pendingFotoPreviewUrl); } catch { /* noop */ }
          }
          setPendingFotoFile(null);
          setPendingFotoPreviewUrl(null);
        }

        if (authUser?.id === editingId) {
          const nextFoto =
            uploadedFotoUrl ??
            (typeof formData.foto === 'string' && formData.foto.trim() !== '' && !formData.foto.startsWith('blob:')
              ? formData.foto
              : authUser?.foto);
          updateUser({
            user_name: formData.user_name,
            nome: formData.user_name,
            foto: nextFoto
          });
        }

        if (!pendingFotoFile && isRemoteHttpUrl(formData.foto)) {
          const salvarResp = await usuarioService.salvarFotoPorUrl(editingId, formData.foto);
          const urlLocal = extractUploadedFotoUrl(salvarResp);
          if (urlLocal) {
            setFormData(prev => ({ ...prev, foto: urlLocal }));
          }
        }

        setSuccess('Usuário atualizado com sucesso!');
      } else {
        const criado = await usuarioService.criar(dados);

        const newId: number | undefined =
          criado?.data?.data?.id ??
          criado?.data?.id ??
          criado?.data?.data?.usuario?.id ??
          criado?.data?.usuario?.id;

        if (pendingFotoFile && newId) {
          const uploadResp = await usuarioService.uploadFoto(newId, pendingFotoFile);
          const url = extractUploadedFotoUrl(uploadResp);
          if (url) {
            setFormData(prev => ({ ...prev, foto: url }));
          }
          if (pendingFotoPreviewUrl) {
            try { URL.revokeObjectURL(pendingFotoPreviewUrl); } catch { /* noop */ }
          }
          setPendingFotoFile(null);
          setPendingFotoPreviewUrl(null);
        }

        if (!pendingFotoFile && isRemoteHttpUrl(formData.foto)) {
          const salvarResp = await usuarioService.salvarFotoPorUrl(newId, formData.foto);
          const urlLocal = extractUploadedFotoUrl(salvarResp);
          if (urlLocal) {
            setFormData(prev => ({ ...prev, foto: urlLocal }));
          }
        }

        setSuccess('Usuário criado com sucesso!');
      }

      await carregarDados();
      setShowForm(false);

      setTimeout(() => setSuccess(''), 3000);

    } catch (error: any) {
      console.error(' [DEBUG] Erro completo ao salvar usuário:', error);
      console.error(' [DEBUG] Response data:', error.response?.data);
      console.error(' [DEBUG] Response status:', error.response?.status);

      let errorMessage = 'Erro ao salvar usuário';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este usuário?')) {
      return;
    }

    try {
      await usuarioService.excluir(id);
      setSuccess('Usuário excluído com sucesso!');
      await carregarDados();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      console.error('Erro ao excluir usuário:', error);
      setError(error.response?.data?.message || 'Erro ao excluir usuário');
    }
  };

  // Função segura para obter inicial do nome
  const getInitial = (name: string | null | undefined): string => {
    if (!name || typeof name !== 'string' || name.length === 0) {
      return 'U';
    }
    return name.charAt(0).toUpperCase();
  };

  // Função para upload de imagem
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>, tipo: 'foto' | 'assinatura') => {
    const file = event.target.files?.[0];
    if (file) {
      if (tipo === 'foto') {
        if (pendingFotoPreviewUrl) {
          try { URL.revokeObjectURL(pendingFotoPreviewUrl); } catch { /* noop */ }
        }
        const previewUrl = URL.createObjectURL(file);
        setPendingFotoFile(file);
        setPendingFotoPreviewUrl(previewUrl);
        setFormData(prev => ({ ...prev, foto: previewUrl }));
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setFormData(prev => ({
          ...prev,
          [tipo]: base64String
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Função para mudança de aba
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Funções de formatação
  const formatCelular = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    return value;
  };

  const formatCPF = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return value;
  };

  const formatCEP = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 8) {
      return numbers.replace(/(\d{5})(\d{3})/, '$1-$2');
    }
    return value;
  };

  // Funções de validação
  const validateEmail = (email: string): boolean => {
    if (!email) return true; // Vazio é válido
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateCelular = (celular: string): boolean => {
    if (!celular) return true; // Vazio é válido
    const numbers = celular.replace(/\D/g, '');
    return numbers.length === 11;
  };

  const validateCPF = (cpf: string): boolean => {
    if (!cpf) return true; // Vazio é válido
    const numbers = cpf.replace(/\D/g, '');
    
    if (numbers.length !== 11) return false;
    
    // Verifica se todos os dígitos são iguais
    if (/^(\d)\1{10}$/.test(numbers)) return false;
    
    // Validação do primeiro dígito verificador
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(numbers[i]) * (10 - i);
    }
    let remainder = sum % 11;
    let digit1 = remainder < 2 ? 0 : 11 - remainder;
    
    if (parseInt(numbers[9]) !== digit1) return false;
    
    // Validação do segundo dígito verificador
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(numbers[i]) * (11 - i);
    }
    remainder = sum % 11;
    let digit2 = remainder < 2 ? 0 : 11 - remainder;
    
    return parseInt(numbers[10]) === digit2;
  };

  const validateCEP = (cep: string): boolean => {
    if (!cep) return true; // Vazio é válido
    const numbers = cep.replace(/\D/g, '');
    return numbers.length === 8;
  };

  const validateDataNascimento = (data: string): boolean => {
    if (!data) return true; // Vazio é válido
    const today = new Date();
    const birthDate = new Date(data);
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      return age - 1 >= 0 && age - 1 <= 120;
    }
    return age >= 0 && age <= 120;
  };

  const validateSenha = (senha: string): boolean => {
    if (!senha) return true; // Vazio é válido para edição
    
    // Mínimo 8 caracteres
    if (senha.length < 8) return false;
    
    // Ao menos uma letra
    if (!/[a-zA-Z]/.test(senha)) return false;
    
    // Ao menos um número
    if (!/[0-9]/.test(senha)) return false;
    
    // Ao menos um caracter especial
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(senha)) return false;
    
    return true;
  };

  // Função para atualizar campo com formatação e validação
  const updateFieldWithValidation = (field: string, value: string) => {
    let formattedValue = value;
    let isValid = true;

    switch (field) {
      case 'celular':
        formattedValue = formatCelular(value);
        isValid = validateCelular(formattedValue);
        break;
      case 'cpf':
        formattedValue = formatCPF(value);
        isValid = validateCPF(formattedValue);
        break;
      case 'cep':
        formattedValue = formatCEP(value);
        isValid = validateCEP(formattedValue);
        break;
      case 'email':
        isValid = validateEmail(value);
        break;
      case 'data_nascimento':
        isValid = validateDataNascimento(value);
        break;
      case 'senha':
        isValid = validateSenha(value);
        break;
    }

    setFormData(prev => ({ ...prev, [field]: formattedValue }));
    setFieldErrors(prev => ({ ...prev, [field]: !isValid && value.length > 0 }));
  };

  // Função para buscar endereço por CEP
  const buscarEnderecoPorCEP = async () => {
    const cep = formData.cep.replace(/\D/g, ''); // Remove caracteres não numéricos
    
    if (cep.length !== 8) {
      setError('CEP deve ter 8 dígitos');
      return;
    }
    
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      
      if (data.erro) {
        setError('CEP não encontrado');
        return;
      }

      // Preencher os campos automaticamente
      setFormData(prev => ({
        ...prev,
        endereco: data.logradouro || '',
        bairro: data.bairro || '',
        cidade: data.localidade || '',
        uf: data.uf || ''
      }));

      setSuccess('Endereço capturado com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
      
      // Colocar foco no campo Número após carregar as informações
      setTimeout(() => {
        const numeroField = document.querySelector('input[data-field="numero"]') as HTMLInputElement;
        if (numeroField) {
          numeroField.focus();
        }
      }, 100);
      
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      setError('Erro ao buscar endereço. Tente novamente.');
    }
  };

  // Função para buscar foto do WhatsApp
  const buscarFotoWhatsApp = async () => {
    try {
      if (!formData.celular) {
        setError('Por favor, preencha o celular primeiro.');
        return;
      }

      // Buscar dados do usuário aziel@sistemap.com.br para obter instancia e token
      const usuariosResponse = await usuarioService.listar();
      const usuarios = usuariosResponse?.data?.data || [];
      
      const usuarioAziel = usuarios.find((u: any) => u.email === 'aziel@sistemap.com.br');
      if (!usuarioAziel) {
        setError('Usuário aziel@sistemap.com.br não encontrado para obter configurações do WhatsApp.');
        return;
      }

      const instancia = usuarioAziel.instancia_whatsapp;
      const token = usuarioAziel.token_whatsapp;

      if (!instancia || !token) {
        setError('Usuário aziel@sistemap.com.br não tem instância e token do WhatsApp configurados.');
        return;
      }

      // Formatar número para padrão W-API
      const formatarNumeroParaWAPI = (telefone: string) => {
        if (!telefone || telefone.trim() === '') {
          return null;
        }

        // Remove todos os caracteres não numéricos
        let numeroLimpo = telefone.replace(/\D/g, '');

        // Remove códigos de país se presentes
        if (numeroLimpo.startsWith('55') && numeroLimpo.length > 11) {
          numeroLimpo = numeroLimpo.substring(2);
        }

        // Verificar se o número tem tamanho válido (10 ou 11 dígitos)
        if (numeroLimpo.length < 10 || numeroLimpo.length > 11) {
          return null;
        }

        // Adicionar código do país (Brasil = 55)
        return '55' + numeroLimpo;
      };

      const numeroFormatado = formatarNumeroParaWAPI(formData.celular);
      if (!numeroFormatado) {
        setError('Formato de número inválido! Use: (11) 98765-4321');
        return;
      }

      console.log(' [DEBUG] Buscando foto do WhatsApp:', {
        instanceId: instancia,
        phoneNumber: numeroFormatado,
        token: token ? '***' : 'NÃO CONFIGURADO'
      });

      // Fazer requisição para a API do WhatsApp
      const response = await fetch(`https://api.w-api.app/v1/contacts/profile-picture?instanceId=${instancia}&phoneNumber=${numeroFormatado}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      console.log(' [DEBUG] Resposta da API WhatsApp:', response.status, response.statusText);

      if (response.ok) {
        const resultado = await response.json();
        console.log(' [DEBUG] Resultado da API:', resultado);
        
        const link = extractWhatsAppLink(resultado);
        if (link) {
          console.log(' [DEBUG] Foto encontrada (URL): ' + link);

          try {
            const imagemResponse = await fetch(link);
            console.log(' [DEBUG] Resposta ao baixar imagem:', imagemResponse.status, imagemResponse.statusText);

            if (imagemResponse.ok) {
              const blob = await imagemResponse.blob();
              const fileName = `whatsapp-foto-${Date.now()}.jpg`;
              const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });

              if (pendingFotoPreviewUrl) {
                try { URL.revokeObjectURL(pendingFotoPreviewUrl); } catch { /* noop */ }
              }

              const previewUrl = URL.createObjectURL(file);
              setPendingFotoFile(file);
              setPendingFotoPreviewUrl(previewUrl);
              setFormData(prev => ({ ...prev, foto: previewUrl }));

              setSuccess('Foto do WhatsApp carregada com sucesso!');
              setTimeout(() => setSuccess(''), 3000);
            } else {
              setError('Erro ao baixar a imagem do WhatsApp.');
            }
          } catch (erroDownload) {
            console.error(' [DEBUG] Erro ao baixar imagem do WhatsApp:', erroDownload);
            setError('Erro ao baixar a imagem do WhatsApp.');
          }
        } else {
          setError('Nenhuma foto encontrada para este número no WhatsApp.');
        }
      } else {
        // Tratamento de erro
        let mensagemErro = '';
        if (response.status === 401) {
          mensagemErro = 'Token inválido ou expirado - Verifique configuração do usuário aziel@sistemap.com.br';
        } else if (response.status === 400) {
          mensagemErro = 'Instância não encontrada ou inválida - Verifique configuração do usuário aziel@sistemap.com.br';
        } else if (response.status === 500) {
          mensagemErro = 'Erro interno da API - usuário pode ter bloqueado a captura da foto';
        } else {
          const errorText = await response.text();
          mensagemErro = `Erro HTTP ${response.status}: ${errorText}`;
        }
        
        console.error(' [DEBUG] Erro na resposta:', response.status, mensagemErro);
        setError(`Erro ao buscar foto do WhatsApp: ${mensagemErro}`);
        
        // Se for erro 500 (usuário bloqueou), remover mensagem após 6 segundos
        if (response.status === 500) {
          setTimeout(() => setError(''), 6000);
        }
      }
    } catch (error) {
      console.error(' [DEBUG] Erro ao buscar foto do WhatsApp:', error);
      setError('Erro ao buscar foto do WhatsApp. Verifique o console para mais detalhes.');
    }
  };

  // Função para abrir modal de localização
  const abrirLocalizacao = () => {
    if (!formData.endereco || !formData.cidade) {
      setError('Preencha o endereço e cidade para visualizar a localização');
      return;
    }
    setShowMapModal(true);
  };

  // Função para importar modelo de contrato do usuário aziel@sistemap.com.br
  const importarModeloContrato = async () => {
    try {
      setLoading(true);
      
      // Buscar usuário aziel@sistemap.com.br
      const usuariosResponse = await usuarioService.listar();
      const usuarios = usuariosResponse?.data?.data || [];
      
      const usuarioAziel = usuarios.find((u: any) => u.email === 'aziel@sistemap.com.br');
      if (!usuarioAziel) {
        setError('Usuário aziel@sistemap.com.br não encontrado para importar modelo de contrato.');
        return;
      }

      if (!usuarioAziel.contrato) {
        setError('Usuário aziel@sistemap.com.br não possui modelo de contrato cadastrado.');
        return;
      }

      // Importar contrato para o campo atual
      setFormData(prev => ({
        ...prev,
        contrato: usuarioAziel.contrato
      }));

      setSuccess('Modelo de contrato importado com sucesso!');
      setTimeout(() => setSuccess(''), 3000);

    } catch (error: any) {
      console.error('Erro ao importar modelo de contrato:', error);
      setError('Erro ao importar modelo de contrato: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setLoading(false);
    }
  };

  // Renderizar formulário
  if (showForm) {
    return (
      <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleCancel} sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
          <PersonIcon sx={{ mr: 2, fontSize: 32 }} />
          <Typography variant="h4" component="h1">
            {editingId ? 'Editar Usuário' : 'Novo Usuário'}
              </Typography>
            </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Card>
          <CardContent>
            {/* Abas */}
            <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, minHeight: 16 }}>
              <Tab 
                label="Dados Cadastrais" 
                icon={<PersonIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="Endereço" 
                icon={<LocationIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="Configurações" 
                icon={<SettingsIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="WhatsApp Baileys" 
                icon={<WhatsAppIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="Contrato" 
                icon={<DescriptionIcon />} 
                iconPosition="start"
              />
            </Tabs>

            <form onSubmit={handleSubmit}>
              {/* ABA 1 - DADOS CADASTRAIS */}
              {activeTab === 0 && (
              <Grid container spacing={3}>
                  {/* Upload de Foto */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '160px', justifyContent: 'space-between' }}>
                      <Box sx={{ position: 'relative' }}>
                        <Avatar
                          src={resolveImageSrc(formData.foto || formData.foto_url)}
                          sx={{ width: 100, height: 100 }}
                        >
                          {getInitial(formData.user_name)}
                        </Avatar>
                        {formData.foto && (
                          <IconButton
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              backgroundColor: '#f44336',
                              color: 'white',
                              width: 20,
                              height: 20,
                              '&:hover': {
                                backgroundColor: '#d32f2f'
                              }
                            }}
                            onClick={() => {
                              if (pendingFotoPreviewUrl) {
                                try { URL.revokeObjectURL(pendingFotoPreviewUrl); } catch { /* noop */ }
                              }
                              setPendingFotoFile(null);
                              setPendingFotoPreviewUrl(null);
                              setFormData(prev => ({ ...prev, foto: '' }));
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 12 }} />
                          </IconButton>
                        )}
                      </Box>
                      <Box>
                        <input
                          accept="image/*"
                          style={{ display: 'none' }}
                          id="foto-upload"
                          type="file"
                          onChange={(e) => handleImageUpload(e, 'foto')}
                        />
                        <label htmlFor="foto-upload">
                          <Button variant="outlined" component="span" startIcon={<PhotoCameraIcon />}>
                            Foto do Usuário
                          </Button>
                        </label>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Upload de Assinatura */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '160px', justifyContent: 'space-between' }}>
                      <Box sx={{ 
                        width: 200, 
                        height: 100, 
                        border: '2px dashed #ccc', 
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#fafafa',
                        position: 'relative'
                      }}>
                        {(() => {
                          console.log('🔍 [DEBUG] Renderizando assinatura:', formData.assinatura ? 'Presente' : 'Ausente');
                          return formData.assinatura;
                        })() ? (
                          <>
                            <img 
                              src={formData.assinatura} 
                              alt="Assinatura" 
                              style={{ 
                                maxWidth: '100%', 
                                maxHeight: '100%', 
                                objectFit: 'contain'
                              }}
                            />
                            <IconButton
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: -8,
                                right: -8,
                                backgroundColor: '#f44336',
                                color: 'white',
                                width: 20,
                                height: 20,
                                '&:hover': {
                                  backgroundColor: '#d32f2f'
                                }
                              }}
                              onClick={() => setFormData(prev => ({ ...prev, assinatura: '' }))}
                            >
                              <CloseIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                          </>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Área da Assinatura
                          </Typography>
                        )}
                      </Box>
                      <Box>
                        <input
                          accept="image/*"
                          style={{ display: 'none' }}
                          id="assinatura-upload"
                          type="file"
                          onChange={(e) => handleImageUpload(e, 'assinatura')}
                        />
                        <label htmlFor="assinatura-upload">
                          <Button variant="outlined" component="span" startIcon={<DrawIcon />}>
                            Assinatura
                          </Button>
                        </label>
                      </Box>
                    </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                      label="Nome Completo"
                      value={formData.user_name}
                      onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
                    required
                    disabled={loading}
                      InputProps={{
                        style: { fontWeight: 'bold' }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Data de Nascimento"
                      type="date"
                      value={formData.data_nascimento}
                      onChange={(e) => updateFieldWithValidation('data_nascimento', e.target.value)}
                      disabled={loading}
                      InputLabelProps={{ shrink: true }}
                      error={fieldErrors.data_nascimento}
                      helperText={fieldErrors.data_nascimento ? "Data inválida" : ""}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Celular"
                      value={formData.celular}
                      onChange={(e) => updateFieldWithValidation('celular', e.target.value)}
                      disabled={loading}
                      placeholder="(00) 00000-0000"
                      error={fieldErrors.celular}
                      helperText={fieldErrors.celular ? "Celular deve ter 11 dígitos" : ""}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Capturar foto WhatsApp">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={buscarFotoWhatsApp}
                                  disabled={loading || !formData.celular}
                                  sx={{ color: '#25D366' }}
                                >
                                  <WhatsAppIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                      label="Email"
                    type="email"
                    value={formData.email}
                      onChange={(e) => updateFieldWithValidation('email', e.target.value)}
                    required
                    disabled={loading}
                    error={fieldErrors.email}
                    helperText={fieldErrors.email ? "Email inválido" : ""}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon />
                          </InputAdornment>
                        ),
                      }}
                  />
                </Grid>

                  <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                      label={editingId ? "Nova Senha (deixe em branco para manter)" : "Senha"}
                      type="password"
                      value={formData.senha}
                      onChange={(e) => updateFieldWithValidation('senha', e.target.value)}
                      required={!editingId}
                      disabled={loading}
                      autoComplete={editingId ? "new-password" : "current-password"}
                      error={fieldErrors.senha}
                      helperText={
                        fieldErrors.senha 
                          ? "Mínimo 8 dígitos (ao menos 1 letra, 1 número, 1 caracter especial)" 
                          : "Mínimo 8 dígitos (ao menos 1 letra, 1 número, 1 caracter especial)"
                      }
                      FormHelperTextProps={{
                        sx: { 
                          fontSize: '0.75rem',
                          color: fieldErrors.senha ? 'error.main' : 'text.secondary'
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="CPF"
                    value={formData.cpf}
                      onChange={(e) => updateFieldWithValidation('cpf', e.target.value)}
                    disabled={loading}
                    error={fieldErrors.cpf}
                    helperText={fieldErrors.cpf ? "CPF inválido" : ""}
                      placeholder="000.000.000-00"
                  />
                </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="RG"
                      value={formData.rg}
                      onChange={(e) => setFormData({ ...formData, rg: e.target.value })}
                    disabled={loading}
                  />
                </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="CRP"
                      value={formData.crp}
                      onChange={(e) => setFormData({ ...formData, crp: e.target.value })}
                    disabled={loading}
                      placeholder="00/00000"
                  />
                </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Função</InputLabel>
                      <Select
                        value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                        disabled={loading}
                        label="Função"
                      >
                        <MenuItem value="ADMIN">Administrador</MenuItem>
                        <MenuItem value="PSICOLOGO">Psicólogo</MenuItem>
                        <MenuItem value="RECEPCIONISTA">Recepcionista</MenuItem>
                        <MenuItem value="USUARIO">Usuário</MenuItem>
                      </Select>
                    </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Nível de Acesso</InputLabel>
                      <Select
                        value={formData.id_nivel_acesso}
                        onChange={(e) => setFormData({ ...formData, id_nivel_acesso: e.target.value })}
                        disabled={loading}
                        label="Nível de Acesso"
                      >
                        {niveisAcesso.map((nivel) => (
                          <MenuItem key={nivel.id} value={nivel.id.toString()}>
                            {nivel.nome}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Plano de Adesão</InputLabel>
                      <Select
                        value={formData.plano_adesao}
                        onChange={(e) => setFormData({ ...formData, plano_adesao: e.target.value })}
                        disabled={loading}
                        label="Plano de Adesão"
                        startAdornment={
                          <InputAdornment position="start">
                            <BusinessIcon />
                            </InputAdornment>
                        }
                      >
                        <MenuItem value="Básico">Básico</MenuItem>
                        <MenuItem value="Profissional">Profissional</MenuItem>
                        <MenuItem value="Premium">Premium</MenuItem>
                        <MenuItem value="Plano Enterprise">Enterprise</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Tipo de Pagamento</InputLabel>
                      <Select
                        value={formData.tipo_pagamento}
                        onChange={(e) => setFormData({ ...formData, tipo_pagamento: e.target.value })}
                        disabled={loading}
                        label="Tipo de Pagamento"
                      >
                        <MenuItem value="PIX">PIX</MenuItem>
                        <MenuItem value="CARTAO">Cartão</MenuItem>
                        <MenuItem value="BOLETO">Boleto</MenuItem>
                        <MenuItem value="DINHEIRO">Dinheiro</MenuItem>
                      </Select>
                    </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                      label="Valor da Mensalidade"
                      type="number"
                      value={formData.valor_mensalidade}
                      onChange={(e) => setFormData({ ...formData, valor_mensalidade: e.target.value })}
                        disabled={loading}
                        InputProps={{
                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                        }}
                      />
                    </Grid>

                <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.ativo}
                          onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="Usuário Ativo"
                    />
                </Grid>
                </Grid>
              )}

              {/* ABA 2 - ENDEREÇO */}
              {activeTab === 1 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={2.4}>
                  <TextField
                    fullWidth
                    label="CEP"
                    value={formData.cep}
                      onChange={(e) => updateFieldWithValidation('cep', e.target.value)}
                    disabled={loading}
                    error={fieldErrors.cep}
                    helperText={fieldErrors.cep ? "CEP deve ter 8 dígitos" : ""}
                      placeholder="00000-000"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Capturar Endereço">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={buscarEnderecoPorCEP}
                                  disabled={loading || !formData.cep}
                                >
                                  <SearchIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                  />
                </Grid>

                  <Grid item xs={12} md={7.2}>
                  <TextField
                    fullWidth
                    label="Endereço"
                    value={formData.endereco}
                      onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                    disabled={loading}
                    InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Abrir localização">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={abrirLocalizacao}
                                  disabled={loading || !formData.endereco || !formData.cidade}
                                >
                                  <LocationIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                  />
                </Grid>

                  <Grid item xs={12} md={2.4}>
                  <TextField
                    fullWidth
                    label="Número"
                    value={formData.numero}
                      onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                    disabled={loading}
                    inputRef={(input) => {
                      if (input) {
                        input.setAttribute('data-field', 'numero');
                      }
                    }}
                  />
                </Grid>



                  <Grid item xs={12} md={4.8}>
                  <TextField
                    fullWidth
                    label="Bairro"
                    value={formData.bairro}
                      onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                    disabled={loading}
                  />
                </Grid>

                  <Grid item xs={12} md={4.8}>
                  <TextField
                    fullWidth
                    label="Cidade"
                    value={formData.cidade}
                      onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                    disabled={loading}
                  />
                </Grid>

                  <Grid item xs={12} md={2.4}>
                  <TextField
                    fullWidth
                      label="UF"
                      value={formData.uf}
                      onChange={(e) => setFormData({ ...formData, uf: e.target.value.toUpperCase() })}
                    disabled={loading}
                      inputProps={{ maxLength: 2 }}
                  />
                </Grid>
                </Grid>
              )}

              {/* ABA 3 - CONFIGURAÇÕES */}
              {activeTab === 2 && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                      Configurações WhatsApp
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Instância WhatsApp"
                      value={formData.instancia_whatsapp}
                      onChange={(e) => setFormData({ ...formData, instancia_whatsapp: e.target.value })}
                      disabled={loading}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Token WhatsApp"
                      value={formData.token_whatsapp}
                      onChange={(e) => setFormData({ ...formData, token_whatsapp: e.target.value })}
                      disabled={loading}
                      type="password"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.envio_mensagens_whatsapp}
                          onChange={(e) => setFormData({ ...formData, envio_mensagens_whatsapp: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="Habilitar envio de mensagens via WhatsApp"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                      Configurações de Segurança
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.login_duas_etapas_whatsapp}
                          onChange={(e) => setFormData({ ...formData, login_duas_etapas_whatsapp: e.target.checked })}
                          disabled={loading}
                        />
                      }
                      label="Login em 2 fatores por WhatsApp"
                    />
                  </Grid>
                </Grid>
              )}

              {/* ABA 4 - WHATSAPP BAILEYS */}
              {activeTab === 3 && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                      Conexão WhatsApp Baileys
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Chip
                          icon={<WhatsAppIcon />}
                          label={getWhatsStatusLabel(whatsStatus)}
                          color={getWhatsStatusColor(whatsStatus)}
                          variant={whatsStatus === 'CONNECTED' ? 'filled' : 'outlined'}
                        />
                        {whatsPhone && (
                          <Chip
                            label={whatsPhone}
                            variant="outlined"
                          />
                        )}
                      </Box>
                      {whatsConnectedAt && (
                        <Typography variant="body2" color="text.secondary">
                          Última conexão: {new Date(whatsConnectedAt).toLocaleString('pt-BR')}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        <Button
                          variant="contained"
                          onClick={iniciarSessaoWhatsApp}
                          disabled={whatsLoading || !editingId}
                        >
                          Gerar/Atualizar QR Code
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => editingId && carregarWhatsAppStatus(editingId)}
                          disabled={whatsLoading || !editingId}
                        >
                          Atualizar status
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={desconectarWhatsApp}
                          disabled={whatsLoading || !editingId || whatsStatus === 'DISCONNECTED'}
                        >
                          Desconectar
                        </Button>
                      </Box>
                      {!editingId && (
                        <Typography variant="body2" color="text.secondary">
                          Salve o usuário primeiro para configurar o WhatsApp Baileys.
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Aponte a câmera do WhatsApp para o QR Code abaixo.
                      </Typography>
                      <Box
                        sx={{
                          width: 260,
                          height: 260,
                          borderRadius: 2,
                          border: '1px solid #ddd',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#fafafa'
                        }}
                      >
                        {whatsQrCode ? (
                          <img
                            src={whatsQrCode}
                            alt="QR Code WhatsApp"
                            style={{ width: '90%', height: '90%', objectFit: 'contain' }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Nenhum QR Code disponível no momento.
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                      Lembretes automáticos de consulta
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={whatsReminders.dayBeforeEnabled}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setWhatsReminders((prev) => ({
                              ...prev,
                              dayBeforeEnabled: checked,
                              dayBeforeTime:
                                checked && !prev.dayBeforeTime ? '18:00:00' : prev.dayBeforeTime
                            }));
                          }}
                          disabled={whatsLoading}
                        />
                      }
                      label="Dia anterior"
                    />
                    <FormControl fullWidth disabled={!whatsReminders.dayBeforeEnabled || whatsLoading}>
                      <InputLabel>Horário</InputLabel>
                      <Select
                        value={whatsReminders.dayBeforeTime || ''}
                        label="Horário"
                        onChange={(e) =>
                          setWhatsReminders((prev) => ({
                            ...prev,
                            dayBeforeTime: e.target.value as string
                          }))
                        }
                      >
                        {HORARIO_OPCOES.map((hora) => (
                          <MenuItem key={hora} value={`${hora}:00`}>
                            {hora}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={whatsReminders.dayOfEnabled}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setWhatsReminders((prev) => ({
                              ...prev,
                              dayOfEnabled: checked,
                              dayOfTime:
                                checked && !prev.dayOfTime ? '07:00:00' : prev.dayOfTime
                            }));
                          }}
                          disabled={whatsLoading}
                        />
                      }
                      label="Dia do atendimento"
                    />
                    <FormControl fullWidth disabled={!whatsReminders.dayOfEnabled || whatsLoading}>
                      <InputLabel>Horário</InputLabel>
                      <Select
                        value={whatsReminders.dayOfTime || ''}
                        label="Horário"
                        onChange={(e) =>
                          setWhatsReminders((prev) => ({
                            ...prev,
                            dayOfTime: e.target.value as string
                          }))
                        }
                      >
                        {HORARIO_OPCOES.map((hora) => (
                          <MenuItem key={hora} value={`${hora}:00`}>
                            {hora}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={whatsReminders.hoursBeforeEnabled}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setWhatsReminders((prev) => ({
                              ...prev,
                              hoursBeforeEnabled: checked,
                              hoursBeforeAmount:
                                checked && !prev.hoursBeforeAmount ? '2' : prev.hoursBeforeAmount
                            }));
                          }}
                          disabled={whatsLoading}
                        />
                      }
                      label="Horas antes"
                    />
                    <TextField
                      fullWidth
                      type="number"
                      label="Quantidade de horas"
                      value={whatsReminders.hoursBeforeAmount}
                      onChange={(e) =>
                        setWhatsReminders((prev) => ({
                          ...prev,
                          hoursBeforeAmount: e.target.value
                        }))
                      }
                      disabled={!whatsReminders.hoursBeforeEnabled || whatsLoading}
                      InputProps={{
                        inputProps: { min: 1, max: 72 }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      label="Mensagem do lembrete"
                      value={whatsReminders.messageTemplate}
                      onChange={(e) =>
                        setWhatsReminders((prev) => ({
                          ...prev,
                          messageTemplate: e.target.value
                        }))
                      }
                      disabled={whatsLoading}
                      helperText="Use {{paciente}}, {{psicologo}}, {{data}} e {{hora}} para personalizar a mensagem."
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Número para teste"
                      value={whatsTestNumber || formData.celular || ''}
                      onChange={(e) => setWhatsTestNumber(e.target.value)}
                      disabled={whatsLoading}
                      placeholder="(00) 00000-0000"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Mensagem de teste"
                      value={whatsTestMessage}
                      onChange={(e) => setWhatsTestMessage(e.target.value)}
                      disabled={whatsLoading}
                      placeholder="Deixe em branco para usar o modelo de lembrete."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        onClick={salvarConfigLembretesWhatsApp}
                        disabled={whatsLoading || !editingId}
                      >
                        Salvar lembretes
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={enviarMensagemTesteWhatsApp}
                        disabled={whatsSendingTest || !editingId}
                        startIcon={whatsSendingTest ? <CircularProgress size={18} /> : <WhatsAppIcon />}
                      >
                        {whatsSendingTest ? 'Enviando teste...' : 'Enviar mensagem de teste'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              )}

              {/* ABA 5 - CONTRATO */}
              {activeTab === 4 && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" component="h2">
                        Contrato do Psicólogo
                      </Typography>
                      
                      {/* Botão Importar Modelo */}
                      <Button
                        variant="contained"
                        size="small"
                        onClick={importarModeloContrato}
                        sx={{ 
                          height: '32px',
                          fontSize: '12px',
                          px: 2
                        }}
                        disabled={loading}
                      >
                        Importar Modelo
                      </Button>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: -1, mb: 1.5, display: 'block', fontSize: '0.75rem' }}>
                      💡 Dicas: Use a barra de ferramentas abaixo para formatar o texto. Você pode aplicar negrito, itálico, alterar tamanhos de fonte, cores, criar listas e muito mais.
                    </Typography>
                    
                    <Box sx={{ 
                      '& .ql-toolbar': {
                        borderTop: '1px solid #ccc',
                        borderLeft: '1px solid #ccc',
                        borderRight: '1px solid #ccc',
                        borderBottom: 'none',
                        borderRadius: '4px 4px 0 0'
                      },
                      '& .ql-container': {
                        borderBottom: '1px solid #ccc',
                        borderLeft: '1px solid #ccc',
                        borderRight: '1px solid #ccc',
                        borderTop: 'none',
                        borderRadius: '0 0 4px 4px',
                        fontSize: '16px',
                        fontFamily: 'Roboto, Arial, sans-serif'
                      },
                      '& .ql-editor': {
                        minHeight: '300px',
                        fontSize: '16px',
                        lineHeight: '1.5',
                        fontFamily: 'Roboto, Arial, sans-serif'
                      },
                      '& .ql-editor.ql-blank::before': {
                        fontSize: '16px',
                        color: '#999',
                        fontStyle: 'normal'
                      }
                    }}>
                      <ReactQuill
                        theme="snow"
                        value={formData.contrato || ''}
                        onChange={(value) => setFormData({ ...formData, contrato: value })}
                        readOnly={loading}
                        modules={{
                          toolbar: [
                            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                            [{ 'font': [] }],
                            [{ 'size': ['small', false, 'large', 'huge'] }],
                            ['bold', 'italic', 'underline', 'strike'],
                            [{ 'color': [] }, { 'background': [] }],
                            [{ 'script': 'sub'}, { 'script': 'super' }],
                            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                            [{ 'indent': '-1'}, { 'indent': '+1' }],
                            [{ 'align': [] }],
                            ['link', 'blockquote', 'code-block'],
                            ['clean']
                          ]
                        }}
                        formats={[
                          'header', 'font', 'size',
                          'bold', 'italic', 'underline', 'strike', 'blockquote',
                          'list', 'bullet', 'indent',
                          'link', 'code-block',
                          'color', 'background',
                          'script', 'align'
                        ]}
                        placeholder="Digite o contrato do psicólogo aqui..."
                      />
                    </Box>
                  </Grid>
                </Grid>
              )}

              {/* Botões de ação */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={loading}
                  startIcon={<CancelIcon />}
                >
                      Cancelar
                    </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || Boolean(formData.senha && fieldErrors.senha)}
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                >
                  {loading ? 'Salvando...' : (editingId ? 'Atualizar' : 'Criar')}
                    </Button>
                  </Box>
            </form>
          </CardContent>
        </Card>

        {/* Modal de Localização */}
        <Dialog 
          open={showMapModal} 
          onClose={() => setShowMapModal(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Localização do Endereço
          </DialogTitle>
          <DialogContent>
            <Box sx={{ width: '100%', height: 400, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Informações do endereço */}
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {formData.endereco}, {formData.numero}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formData.bairro} - {formData.cidade}, {formData.uf}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  CEP: {formData.cep}
                </Typography>
              </Box>
              
              {/* Visualização do Mapa */}
              <Box sx={{ 
                width: '100%', 
                height: 300, 
                border: '2px dashed #ccc', 
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f8f9fa',
                position: 'relative'
              }}>
                <LocationIcon sx={{ fontSize: 48, color: '#1976d2', mb: 2 }} />
                <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
                  Localização do Endereço
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 300 }}>
                  Clique no botão abaixo para abrir a localização no Google Maps
                </Typography>
                
                {/* Indicador de coordenadas simuladas */}
                <Box sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                  padding: 1,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  color: '#1976d2'
                }}>
                  📍 Visualização disponível
                </Box>
              </Box>
              
              {/* Botões de ação */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    const endereco = `${formData.endereco}, ${formData.numero}, ${formData.bairro}, ${formData.cidade}, ${formData.uf}, Brasil`;
                    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(endereco)}`;
                    window.open(url, '_blank');
                  }}
                  startIcon={<LocationIcon />}
                  sx={{ minWidth: 200 }}
                >
                  Abrir no Google Maps
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    const endereco = `${formData.endereco}, ${formData.numero}, ${formData.bairro}, ${formData.cidade}, ${formData.uf}, Brasil`;
                    const url = `https://waze.com/ul?q=${encodeURIComponent(endereco)}`;
                    window.open(url, '_blank');
                  }}
                  sx={{ minWidth: 120 }}
                >
                  Abrir no Waze
                </Button>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowMapModal(false)}>
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Renderizar lista
  return (
    <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate(-1)} color="primary" sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <PersonIcon sx={{ mr: 2, fontSize: 32 }} />
          <Typography variant="h4" component="h1">
            Usuários
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNew}
          disabled={loading}
        >
              Novo Usuário
            </Button>
          </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Carregando usuários...</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>Usuário</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Função</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usuarios && usuarios.length > 0 ? (
                    usuarios.map((usuario) => {
                      // Verificação de segurança
                      if (!usuario || !usuario.id) return null;
                      
                      return (
                      <TableRow key={usuario.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar 
                                src={resolveImageSrc((usuario as any)?.foto || (usuario as any)?.foto_url)}
                                sx={{ width: 40, height: 40, mr: 2 }}
                              >
                                {getInitial(usuario.user_name)}
                            </Avatar>
                              <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {usuario.user_name || 'Nome não informado'}
                            </Typography>
                                {usuario.celular && (
                                  <Typography variant="caption" color="text.secondary">
                                    {usuario.celular}
                                  </Typography>
                                )}
                              </Box>
                          </Box>
                        </TableCell>
                          <TableCell>
                            {usuario.email || 'Email não informado'}
                          </TableCell>
                        <TableCell>
                          <Chip 
                              label={usuario.role || 'Não definido'}
                            size="small" 
                              color={usuario.role === 'ADMIN' ? 'error' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={usuario.ativo ? 'Ativo' : 'Inativo'} 
                            size="small" 
                              color={usuario.ativo ? 'success' : 'error'}
                            sx={{
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          />
                        </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(usuario)}
                              color="primary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(usuario.id)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                        </TableCell>
                      </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Nenhum usuário cadastrado
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Usuarios;
